import {
  buildProjectUrl,
  UserSoundClient,
  UserSoundPortalLoadError,
  validatePortalUrl,
  PORTAL_LOAD_ERROR_EVENT_TYPE,
  isPortalLoadErrorEvent,
  type PortalEvent,
  type PortalLoadErrorEvent,
} from "../core/index.js";
import { IframeTransport } from "./iframe-transport.js";
import {
  installWidgetSessionPersistence,
  resolveResponseIdForPortalUrl,
  isIdentifiedPortalContext,
} from "./widget-session.js";

/**
 * Free-form context passed to the portal. Known optional fields are
 * `user_id`, `response_id` (server session id for URL path resume), `first_name`,
 * `last_name`, and `email`; any other string keys are allowed for custom context.
 */
export interface PortalContext {
  user_id?: string;
  /** When set, included in the survey path as `/survey/{project}/{response_id}/`. */
  response_id?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  [key: string]: string | undefined;
}

export type BuildPortalUrlOptions = {
  /** Skip reading anonymous session id from localStorage (e.g. tests). */
  skipAnonymousSessionLookup?: boolean;
};

export interface UserSoundPortalOptions {
  /** UserSound project UUID. */
  projectId: string;
  /**
   * Optional context for the survey (e.g. user_id, first_name, last_name, email).
   * All fields are optional; additional string keys are allowed.
   */
  context?: PortalContext;
  /**
   * Called when the portal emits events (e.g. started, finished).
   */
  onEvent?: (event: PortalEvent) => void;
  /**
   * Override the portal base URL (e.g. for self-hosted or staging).
   */
  baseUrl?: string;
  /**
   * Called when the iframe reports its content size (postMessage with type
   * {@link IFRAME_RESIZE_MESSAGE_TYPE} and `width`/`height`). Use this to size
   * host UI (e.g. dialogs) to fit the embedded content.
   */
  onResize?: (width: number, height: number) => void;
  /**
   * Called when the portal URL fails to load (e.g. invalid URL, 404, network error).
   * Use this for logging or to show a custom error.
   */
  onPortalLoadError?: (error: UserSoundPortalLoadError) => void;
}

export interface UserSoundPortalInstance {
  /**
   * Mounts the portal into a container element. Creates and manages the iframe.
   * Can be called multiple times to move the iframe between containers.
   */
  mount(container: HTMLElement): void;
  /**
   * Shows the portal iframe (if it has been mounted).
   */
  open(): void;
  /**
   * Hides the portal iframe without destroying it.
   */
  close(): void;
  /**
   * Updates the survey context. Takes effect for subsequent events and
   * may trigger a reload of the underlying portal URL.
   */
  updateContext(context: PortalContext): void;
  /**
   * Destroys the portal iframe and removes all event listeners.
   */
  destroy(): void;
}

/** Message type the iframe can send so the host can size UI around it. */
export const IFRAME_RESIZE_MESSAGE_TYPE = "usersound:resize";

export function buildPortalUrl(
  projectId: string,
  context: PortalContext,
  baseUrl?: string,
  options?: BuildPortalUrlOptions
): string {
  const rid = resolveResponseIdForPortalUrl(projectId, context, options);
  const base = buildProjectUrl(
    projectId,
    context.user_id ?? "",
    baseUrl,
    rid
  );
  const url = new URL(base);
  for (const [key, value] of Object.entries(context)) {
    if (
      value != null &&
      value !== "" &&
      key !== "user_id" &&
      key !== "response_id"
    ) {
      url.searchParams.set(key, String(value));
    }
  }
  return url.toString();
}

function tryParse(value: unknown): unknown {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

/**
 * Creates a framework-agnostic UserSound portal instance that can be controlled
 * from plain JavaScript in the browser.
 */
export function createUserSoundPortal(
  options: UserSoundPortalOptions
): UserSoundPortalInstance {
  if (typeof window === "undefined" || typeof document === "undefined") {
    throw new Error(
      "createUserSoundPortal can only be used in a browser environment"
    );
  }

  let currentContext: PortalContext = options.context ?? {};
  let iframe: HTMLIFrameElement | null = null;
  let lastSrc: string | null = null;
  const iframeRef: { current: HTMLIFrameElement | null } = { current: null };
  const transport = new IframeTransport(iframeRef);
  const client = new UserSoundClient(transport);

  let removeResizeListener: (() => void) | null = null;
  let removeWidgetSessionListener: (() => void) | null = null;
  let removeEventListener: (() => void) | null = null;

  const attachResizeListener = () => {
    if (!options.onResize) return;
    if (removeResizeListener) return;
    const listener = (e: MessageEvent) => {
      if (e.source !== iframe?.contentWindow) return;
      const data = tryParse(e.data);
      if (
        data &&
        typeof data === "object" &&
        (data as Record<string, unknown>).type === IFRAME_RESIZE_MESSAGE_TYPE &&
        typeof (data as Record<string, unknown>).width === "number" &&
        typeof (data as Record<string, unknown>).height === "number"
      ) {
        options.onResize?.(
          (data as Record<string, number>).width,
          (data as Record<string, number>).height
        );
      }
    };
    window.addEventListener("message", listener);
    removeResizeListener = () =>
      window.removeEventListener("message", listener);
  };

  const detachResizeListener = () => {
    if (removeResizeListener) {
      removeResizeListener();
      removeResizeListener = null;
    }
  };

  if (options.onEvent) {
    removeEventListener = client.onEvent(options.onEvent);
  }

  removeWidgetSessionListener = installWidgetSessionPersistence({
    projectId: options.projectId,
    baseUrl: options.baseUrl,
    isAnonymous: () => !isIdentifiedPortalContext(currentContext),
  });

  const ensureIframe = () => {
    if (iframe && iframe.ownerDocument) {
      return iframe;
    }
    const el = document.createElement("iframe");
    el.style.border = "none";
    el.style.width = "100%";
    el.style.height = "100%";
    el.title = "UserSound Portal";
    el.allow = "microphone";
    iframe = el;
    iframeRef.current = el;
    return el;
  };

  const setSrcFromContext = () => {
    if (!iframe) return;
    let uri: string;
    try {
      uri = buildPortalUrl(
        options.projectId,
        currentContext,
        options.baseUrl
      );
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Invalid survey or base URL";
      const error = new UserSoundPortalLoadError(
        message,
        undefined,
        "invalid_content" as const,
        undefined,
        err
      );
      options.onPortalLoadError?.(error);
      client.handleMessage(JSON.stringify(error.toEventPayload()));
      return;
    }

    if (lastSrc === uri) return;

    validatePortalUrl(uri)
      .then(() => {
        if (iframe) {
          iframe.src = uri;
          lastSrc = uri;
        }
      })
      .catch((err: unknown) => {
        const error =
          err instanceof UserSoundPortalLoadError
            ? err
            : new UserSoundPortalLoadError(
                err instanceof Error ? err.message : "Failed to load survey",
                uri,
                "network_error",
                undefined,
                err
              );
        options.onPortalLoadError?.(error);
        client.handleMessage(JSON.stringify(error.toEventPayload()));
      });
  };

  return {
    mount(container: HTMLElement) {
      const el = ensureIframe();
      if (!container.contains(el)) {
        container.appendChild(el);
      }
      setSrcFromContext();
      attachResizeListener();
    },
    open() {
      const el = ensureIframe();
      if (!el.parentElement) return;
      el.style.display = "";
    },
    close() {
      if (!iframe) return;
      iframe.style.display = "none";
    },
    updateContext(context: PortalContext) {
      currentContext = { ...context };
      setSrcFromContext();
    },
    destroy() {
      detachResizeListener();
      if (removeWidgetSessionListener) {
        removeWidgetSessionListener();
        removeWidgetSessionListener = null;
      }
      if (removeEventListener) {
        removeEventListener();
        removeEventListener = null;
      }
      if (iframe && iframe.parentElement) {
        iframe.parentElement.removeChild(iframe);
      }
      iframe = null;
      iframeRef.current = null;
      lastSrc = null;
    },
  };
}

export {
  UserSoundClient,
  UserSoundPortalLoadError,
  PORTAL_LOAD_ERROR_EVENT_TYPE,
  isPortalLoadErrorEvent,
};
export type { PortalEvent, PortalLoadErrorEvent };
